import React, { useEffect, useState } from "react";
import { Modal, Box, Typography, Stack, TextField, MenuItem, Button, Fade, Backdrop, alpha, Paper, IconButton, Grid, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function ModalFormUniversal({
  open,
  onClose,
  title = "Formulario",
  fields = [],
  initialData = {},
  onSubmit,
}) {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Aplicar valores por defecto de los campos si no existen en initialData
    const formWithDefaults = { ...initialData };
    fields.forEach((field) => {
      if (field.defaultValue !== undefined && formWithDefaults[field.name] === undefined) {
        formWithDefaults[field.name] = field.defaultValue;
      }
    });
    setForm(formWithDefaults);
    setErrors({});
  }, [initialData, fields]);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" })); // Limpiar error al escribir
  };

  const validateField = (field, value) => {
    // Validar required
    if (field.required) {
      const isEmpty =
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (field.type === "select" && value === "none");

      if (isEmpty) return "Este campo es obligatorio";
    }

    // Validar email
    if (field.type === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) return "Email inválido";
    }

    // Validar number
    if (field.type === "number" && value !== "") {
      if (isNaN(value)) return "Debe ser un número válido";
      if (field.min !== undefined && value < field.min)
        return `Mínimo ${field.min}`;
      if (field.max !== undefined && value > field.max)
        return `Máximo ${field.max}`;
    }

    // Validar pattern en text
    if (field.type === "text" && field.pattern && value) {
      const regex = new RegExp(field.pattern);
      if (!regex.test(value.trim())) return "Formato inválido";
    }

    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    fields.forEach((f) => {
      const error = validateField(f, form[f.name]);
      if (error) newErrors[f.name] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSubmit(form);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      BackdropProps={{
        sx: {
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 400 },
            maxHeight: "85vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            }}
          >
            {/* Header limpio */}
            <Box
              sx={{
                background: "#fff",
                p: 2,
                borderBottom: `1px solid ${alpha("#000", 0.08)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "text.primary" }}>
                {title}
              </Typography>
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    background: alpha("#000", 0.05),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Form Content con scroll */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 2.5,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: alpha("#1976d2", 0.05),
                },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha("#1976d2", 0.2),
                  borderRadius: 1,
                  "&:hover": {
                    background: alpha("#1976d2", 0.3),
                  },
                },
              }}
            >
              <Stack spacing={2}>
                {(() => {
                  // Separar campos normales y compactos
                  const normalFields = fields.filter((f) => !f.compact);
                  const compactFields = fields.filter((f) => f.compact);

                  return (
                    <>
                      {/* Campos normales */}
                      {normalFields.map((f) => {
                        const value = form[f.name] ?? "";
                        const error = errors[f.name] || "";

                        if (f.type === "autocomplete") {
                          return (
                            <Autocomplete
                              key={f.name}
                              freeSolo
                              options={f.options || []}
                              value={value || ""}
                              onChange={(event, newValue) => {
                                handleChange(f.name, newValue || "");
                              }}
                              onInputChange={(event, newInputValue) => {
                                handleChange(f.name, newInputValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label={f.label}
                                  error={!!error}
                                  helperText={error || f.helperText}
                                  disabled={f.disabled}
                                />
                              )}
                            />
                          );
                        }

                        if (f.type === "select") {
                          return (
                            <TextField
                              key={f.name}
                              select
                              size="small"
                              label={f.label}
                              value={value || ""}
                              onChange={(e) => handleChange(f.name, e.target.value)}
                              error={!!error}
                              disabled={f.disabled}
                              helperText={error || f.helperText}
                              fullWidth
                            >
                              {f.allowNone && <MenuItem value="none">Ninguno</MenuItem>}
                              {f.options?.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }

                        return (
                          <TextField
                            key={f.name}
                            size="small"
                            label={f.label}
                            type={f.type || "text"}
                            value={value}
                            onChange={(e) =>
                              handleChange(
                                f.name,
                                f.type === "number" ? Number(e.target.value) : e.target.value
                              )
                            }
                            error={!!error}
                            helperText={error || f.helperText}
                            fullWidth
                            multiline={f.type === "text" && f.rows}
                            rows={f.rows}
                          />
                        );
                      })}

                      {/* Campos compactos en grid de 2 columnas */}
                      {compactFields.length > 0 && (
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
                          {compactFields.map((f) => {
                            const value = form[f.name] ?? "";
                            const error = errors[f.name] || "";

                            if (f.type === "autocomplete") {
                              return (
                                <Box key={f.name} sx={{ flex: "1 1 calc(50% - 8px)", minWidth: 0 }}>
                                  <Autocomplete
                                    freeSolo
                                    options={f.options || []}
                                    value={value || ""}
                                    onChange={(event, newValue) => {
                                      handleChange(f.name, newValue || "");
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                      handleChange(f.name, newInputValue);
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        label={f.label}
                                        error={!!error}
                                        helperText={error || f.helperText}
                                        disabled={f.disabled}
                                      />
                                    )}
                                  />
                                </Box>
                              );
                            }

                            if (f.type === "select") {
                              return (
                                <Box key={f.name} sx={{ flex: "1 1 calc(50% - 8px)", minWidth: 0 }}>
                                  <TextField
                                    select
                                    size="small"
                                    label={f.label}
                                    value={value || ""}
                                    onChange={(e) => handleChange(f.name, e.target.value)}
                                    error={!!error}
                                    disabled={f.disabled}
                                    helperText={error || f.helperText}
                                    fullWidth
                                  >
                                    {f.allowNone && <MenuItem value="none">Ninguno</MenuItem>}
                                    {f.options?.map((opt) => (
                                      <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Box>
                              );
                            }

                            return (
                              <Box key={f.name} sx={{ flex: "1 1 calc(50% - 8px)", minWidth: 0 }}>
                                <TextField
                                  size="small"
                                  label={f.label}
                                  type={f.type || "text"}
                                  value={value}
                                  onChange={(e) =>
                                    handleChange(
                                      f.name,
                                      f.type === "number" ? Number(e.target.value) : e.target.value
                                    )
                                  }
                                  error={!!error}
                                  helperText={error || f.helperText}
                                  fullWidth
                                  multiline={f.type === "text" && f.rows}
                                  rows={f.rows}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Stack>
            </Box>

            {/* Footer limpio */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${alpha("#000", 0.08)}`,
                background: "#fff",
                flexShrink: 0,
                display: "flex",
                gap: 1.5,
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={onClose} size="small" sx={{ fontWeight: 600, minWidth: 80 }}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                size="small"
                sx={{
                  background: "#1976d2",
                  fontWeight: 700,
                  minWidth: 100,
                  "&:hover": {
                    background: "#1565c0",
                  },
                }}
              >
                Guardar
              </Button>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
}
